/**
 * Shared filesystem watcher factory for mountable adapters.
 *
 * Uses chokidar for cross-platform, debounced file watching.
 *
 * Automatically reads .gitignore from the watched directory and merges
 * those patterns into chokidar's ignore list. This prevents scanning
 * large generated directories (node_modules, dist, .next, etc.) that
 * most projects already declare as ignored.
 */

import { readFile } from "node:fs/promises";
import { join, relative } from "node:path";
import { tryWithFsDeadline } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
// Static import so bun --compile bundles chokidar into the binary. A dynamic
// `await import("chokidar")` is not seen by the compiler's static analyzer
// and fails at runtime inside the /$bunfs/ virtual filesystem.
import chokidar from "chokidar";
import type { ConnectorChangeEvent } from "./connector-types.js";
import type { WatchHandle, WatchOptions } from "./shared-types.js";

/**
 * Convert a glob pattern to a matcher function.
 *
 * Chokidar 5 changed string matchers to exact equality (`===`) instead of
 * glob matching. We convert our patterns to functions so chokidar can still
 * filter correctly.
 */
function globToMatcher(pattern: string): (path: string) => boolean {
  // Simple segment-name patterns: **/NAME/** or **/NAME
  // Match any path that contains /NAME/ or ends with /NAME
  const segmentMatch = pattern.match(/^\*\*\/([^*/?]+)\/\*\*$/);
  if (segmentMatch) {
    const name = segmentMatch[1];
    return (p: string) => p.includes(`/${name}/`) || p.endsWith(`/${name}`) || p === name;
  }

  // Dot-prefixed segment: **/.NAME or **/.NAME/**
  const dotSegment = pattern.match(/^\*\*\/(\.[\w#]+)(?:\/\*\*)?$/);
  if (dotSegment) {
    const name = dotSegment[1];
    return (p: string) => p.includes(`/${name}/`) || p.endsWith(`/${name}`) || p === name;
  }

  // Extension wildcard: **/*.EXT.* or **/*.EXT
  const extMatch = pattern.match(/^\*\*\/\*(\.\w+(?:\.\*)?)/);
  if (extMatch) {
    const suffix = extMatch[1];
    if (suffix.endsWith(".*")) {
      // e.g. **/*.tmp.* -> match any file with .tmp. in it
      const base = suffix.slice(0, -2);
      return (p: string) => p.includes(base);
    }
    return (p: string) => p.endsWith(suffix);
  }

  // Suffix wildcard: **/*SUFFIX (e.g. **/*~)
  const suffixMatch = pattern.match(/^\*\*\/\*(.+)$/);
  if (suffixMatch) {
    const sfx = suffixMatch[1];
    return (p: string) => p.endsWith(sfx);
  }

  // Fallback: convert simple globs to regex
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, "\\$&")
    .replace(/\*\*/g, "{{GLOBSTAR}}")
    .replace(/\*/g, "[^/]*")
    .replace(/\?/g, "[^/]")
    .replace(/\{\{GLOBSTAR\}\}/g, ".*");
  const re = new RegExp(`^${escaped}$`);
  return (p: string) => re.test(p);
}

const DEFAULT_IGNORED: Array<string | ((path: string) => boolean)> = [
  (p: string) => p.includes("/.git/") || p.endsWith("/.git"),
  (p: string) => p.includes("/node_modules/") || p.endsWith("/node_modules"),
  (p: string) => p.includes("/.connectors/") || p.endsWith("/.connectors"),
  // Atomic-write tempfiles (Bun.write creates `<name>.tmp.<pid>.<ts>` then renames).
  // These vanish between readdir and fs.watch, causing EINVAL that crashes the process.
  (p: string) => /\.tmp\.\d/.test(p),
  // Editor swap/backup files that also race with save operations.
  (p: string) => p.endsWith(".swp"),
  (p: string) => p.endsWith(".swx"),
  (p: string) => p.includes("/.#"),
  (p: string) => p.endsWith("~"),
];
/** Default chokidar `awaitWriteFinish` stability threshold (ms). */
export const DEFAULT_DEBOUNCE_MS = 150;

/**
 * Parse a .gitignore file into glob patterns suitable for chokidar's `ignored` option.
 * Handles comments, blank lines, directory patterns, and root-relative patterns.
 * Negation patterns (!) are skipped - chokidar cannot un-ignore.
 *
 * `rootDir` is a connector mount path, which in the agent container can be an
 * rclone FUSE mount — hence async + deadline-bounded, with a missing or
 * unreadable file degrading to "no extra ignores".
 */
async function parseGitignore(rootDir: string): Promise<Array<(path: string) => boolean>> {
  const gitignorePath = join(rootDir, ".gitignore");
  const content = await tryWithFsDeadline<string | null>(
    `read ${gitignorePath}`,
    () => readFile(gitignorePath, "utf-8"),
    null,
  );
  if (content === null) return [];

  try {
    const matchers: Array<(path: string) => boolean> = [];

    for (const raw of content.split("\n")) {
      const line = raw.trim();
      if (!line || line.startsWith("#") || line.startsWith("!")) continue;

      let pattern = line;

      // Strip leading / (root-relative -> convert to rootDir-relative)
      if (pattern.startsWith("/")) {
        pattern = pattern.slice(1);
      } else if (!pattern.includes("/")) {
        // Bare name like "node_modules" or "*.log" -> match anywhere
        pattern = `**/${pattern}`;
      }

      // Directory pattern (trailing /) -> ensure it matches contents too
      if (pattern.endsWith("/")) {
        matchers.push(globToMatcher(`${pattern}**`));
      } else {
        matchers.push(globToMatcher(pattern));
        // Also match as directory (e.g. "dist" should match "dist/" and "dist/**")
        if (!pattern.includes("*") && !pattern.includes(".")) {
          matchers.push(globToMatcher(`${pattern}/**`));
        }
      }
    }

    return matchers;
  } catch {
    return [];
  }
}

type ChokidarAction = "create" | "edit" | "delete";

const CHOKIDAR_EVENT_MAP: Record<string, ChokidarAction> = {
  add: "create",
  change: "edit",
  unlink: "delete",
  addDir: "create",
  unlinkDir: "delete",
};

/**
 * Create a cross-platform filesystem watcher on `rootDir` using chokidar.
 * Auto-reads `.gitignore` from `rootDir` and merges those patterns into the ignore list.
 * @param rootDir - Absolute path to the directory to watch.
 * @param callback - Called with each `ConnectorChangeEvent` (create / edit / delete).
 * @param options - Debounce interval and additional ignore patterns.
 * @returns A `WatchHandle` — call `.close()` to stop watching.
 * @docLink packages/connectors/api-reference#create-fs-watcher
 */
export async function createFsWatcher(
  rootDir: string,
  callback: (event: ConnectorChangeEvent) => void,
  options?: WatchOptions,
): Promise<WatchHandle> {
  const log = createLogger({ kind: "connector", subkind: "watcher", instance: rootDir });
  const gitignorePatterns = await parseGitignore(rootDir);
  // Convert any remaining string patterns from options to matcher functions
  // (chokidar 5 treats strings as exact equality, not globs)
  const optionsIgnored = (options?.ignored ?? []).map((entry) =>
    typeof entry === "string" ? globToMatcher(entry) : entry,
  );
  const ignored = [...DEFAULT_IGNORED, ...gitignorePatterns, ...optionsIgnored];
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const watcher = chokidar.watch(rootDir, {
    ignoreInitial: true,
    ignored,
    awaitWriteFinish: { stabilityThreshold: debounceMs, pollInterval: 50 },
    usePolling: false,
  });

  // Swallow transient watch errors (EINVAL/ENOENT when a file vanishes between
  // readdir and fs.watch). Without this handler, chokidar rethrows and the
  // agent process dies.
  watcher.on("error", (err) => {
    const message = err instanceof Error ? err.message : String(err);
    log.warn("watcher error (non-fatal)", { message });
  });

  for (const [chokidarEvent, action] of Object.entries(CHOKIDAR_EVENT_MAP)) {
    (watcher as { on(event: string, fn: (fullPath: string) => void): void }).on(
      chokidarEvent,
      (fullPath: string) => {
        const rel = relative(rootDir, fullPath);
        if (!rel || rel.startsWith("..")) return;
        // Normalize to forward slashes
        const normalizedPath = rel.split("\\").join("/");
        callback({ path: normalizedPath, action, source: "filesystem" });
      },
    );
  }

  return {
    close: () => watcher.close(),
  };
}
